import { Stack } from '@mui/material'
import { useGetMilkingProcessStats } from 'hook/query/useGetMilkingProcessStats'
import { ShiftOptions } from 'models/ICattlesData'
import moment from 'moment'
import { FC, useCallback, useMemo, useState } from 'react'
import { ChartDeviationCard } from './shared/chart-deviations/ChartDeviationsCard'
import { DeviationTableListCard } from './shared/deviation-list-table/DeviationTableListCard'
import { MilkingProcessHeader } from './shared/header/MilkingProcessHeader'
import { InfoBlock } from './shared/info-block/InfoBlock'
import { IMilkingProcessFilters } from './types'

export const MilkingProcess: FC = () => {
	const [filters, setFilters] = useState<IMilkingProcessFilters>({
		parlorId: 'all',
		shift: 'all',
		dateRange: {
			startDate: moment().startOf('day').valueOf(),
			endDate: moment().endOf('day').valueOf(),
		},
		daysBetweenDates: 0,
	})
	const handleFiltersChange = useCallback(
		(newFilters: IMilkingProcessFilters) => {
			setFilters(newFilters)
		},
		[],
	)

	const { data, isLoading } = useGetMilkingProcessStats(
		filters.dateRange.startDate,
		filters.dateRange.endDate,
		filters.parlorId,
		filters.shift,
	)

	const nullShifts = useMemo<ShiftOptions[]>(() => {
		if (!data?.stats || isLoading) return []
		const disabled: ShiftOptions[] = []
		if (data.stats.am === null) disabled.push(ShiftOptions.AM)
		if (data.stats.pm === null) disabled.push(ShiftOptions.PM)
		if (data.stats.night === null) disabled.push(ShiftOptions.NIGHT)
		return disabled
	}, [data?.stats, isLoading])

	const hasNoSourceData = useMemo(() => {
		if (isLoading) return false
		const stats = data?.stats
		if (!stats) return true
		return [
			stats.am,
			stats.pm,
			stats.night,
			stats.connect_avg,
			stats.milking_avg,
		].every(value => value === null || value === undefined)
	}, [data?.stats, isLoading])

	return (
		<Stack direction={'column'} gap={'24px'} sx={{ p: '0 16px' }}>
			<MilkingProcessHeader
				onFiltersChange={handleFiltersChange}
				nullShifts={nullShifts}
			/>

			{filters.dateRange.endDate && filters.dateRange.startDate && (
				<>
					<InfoBlock
						stats={data?.stats}
						deviations={data?.deviations}
						isLoading={isLoading}
						hasNoSourceData={hasNoSourceData}
					/>

					<ChartDeviationCard
						shift={filters.shift}
						daysBetweenDates={filters.daysBetweenDates}
						startDate={filters.dateRange.startDate}
						endDate={filters.dateRange.endDate}
						parlorId={filters.parlorId}
					/>
					<DeviationTableListCard
						shift={filters.shift}
						startDate={filters.dateRange.startDate}
						endDate={filters.dateRange.endDate}
						parlorId={filters.parlorId}
						hasNoSourceData={hasNoSourceData}
					/>
				</>
			)}
		</Stack>
	)
}
